export function createDeliveryTrackingWakeLock(dependencies = {}) {
    const navigatorTarget = dependencies.navigator ?? navigator;
    const documentTarget = dependencies.document ?? document;
    const onState = dependencies.onState ?? (() => {});
    const supported = typeof navigatorTarget.wakeLock?.request === 'function';
    let enabled = false;
    let sharing = false;
    let destroyed = false;
    let state = supported ? 'disabled' : 'unsupported';
    let lock = null;
    let pending = false;
    let revision = 0;
    let removeReleaseListener = null;

    const snapshot = () => ({
        state,
        supported,
        enabled,
        sharing,
        held: lock !== null,
    });
    const emit = (next) => {
        state = next;
        onState(snapshot());
    };
    const shouldHold = () =>
        supported && enabled && sharing && !destroyed && !documentTarget.hidden;
    const release = () => {
        revision += 1;
        removeReleaseListener?.();
        removeReleaseListener = null;
        const old = lock;
        lock = null;
        if (old !== null)
            Promise.resolve()
                .then(() => old.release())
                .catch(() => {});
        emit(supported ? 'disabled' : 'unsupported');
    };
    const reconcile = async () => {
        if (!shouldHold()) {
            release();
            return;
        }
        if (lock !== null || pending) return;
        const currentRevision = revision;
        pending = true;
        emit('requesting');
        try {
            const acquired = await navigatorTarget.wakeLock.request('screen');
            if (currentRevision !== revision || !shouldHold()) {
                await acquired.release();
                return;
            }
            const handleRelease = () => {
                if (lock !== acquired) return;
                lock = null;
                acquired.removeEventListener('release', handleRelease);
                removeReleaseListener = null;
                emit('released');
            };
            acquired.addEventListener('release', handleRelease);
            removeReleaseListener = () =>
                acquired.removeEventListener('release', handleRelease);
            lock = acquired;
            emit('held');
        } catch {
            if (currentRevision === revision && shouldHold())
                emit('unavailable');
        } finally {
            pending = false;
            if (currentRevision !== revision && shouldHold()) void reconcile();
        }
    };
    const handleVisibility = () => {
        void reconcile();
    };
    documentTarget.addEventListener('visibilitychange', handleVisibility);

    return {
        snapshot,
        setEnabled(value) {
            if (destroyed) return;
            enabled = value === true;
            void reconcile();
        },
        setSharing(value) {
            if (destroyed) return;
            sharing = value === true;
            void reconcile();
        },
        destroy() {
            if (destroyed) return;
            destroyed = true;
            sharing = false;
            documentTarget.removeEventListener(
                'visibilitychange',
                handleVisibility,
            );
            release();
        },
    };
}
